import { useParams, useSearchParams } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltSubpageHeader } from "../ui/VoltSubpageHeader";
import { WorkshopError } from "../workshop/WorkshopError";
import { FundingEdit } from "./FundingEdit";
import { listLink, yearOf } from "./fundingLinks";
import { FundingDenied, FundingLoading } from "./FundingStates";
import { PointsBreakdown } from "./FundingScale";
import { eur, placeOf } from "./fundingText";
import { useFundingList, useFundingScale } from "./useFundingList";

/** One application as a subpage: statement of the department, our rating, editing. */
export const FundingDetailPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { data, error, denied, reload, update } = useFundingList(
    yearOf(params),
  );
  const scale = useFundingScale(!!data);

  if (denied !== null) return <FundingDenied reason={denied} />;
  if (!data) {
    if (error) return <WorkshopError>{error}</WorkshopError>;
    return <FundingLoading />;
  }
  const back = {
    to: listLink("/funding-list", params, data.liste.foerderjahr),
    label: "Alle Anträge",
  };
  const row = data.zeilen.find((r) => String(r.antragId) === id);
  if (!row) {
    return (
      <div className="flex flex-col gap-4">
        <VoltSubpageHeader back={back} title="Antrag nicht gefunden" />
        <VoltEmpty
          title="Diesen Antrag gibt es im Förderjahr nicht"
          description={`Förderjahr ${data.liste.foerderjahr}`}
        />
      </div>
    );
  }
  const b = row.bewertung;
  const version = row.version;
  const place = placeOf(row);

  return (
    <div className="flex flex-col gap-5">
      <VoltSubpageHeader
        back={back}
        title={row.titel ?? "Ohne Titel"}
        description={[
          `Nr. ${row.nr}`,
          row.traeger,
          place,
          `beantragt ${eur(row.beantragt)}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
      {error ? <WorkshopError>{error}</WorkshopError> : null}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Stellungnahme Fachamt</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-line text-muted-foreground">
                {row.fachamt ?? "keine"}
              </p>
            </CardContent>
          </Card>
          {version !== null ? (
            <Card>
              <CardHeader>
                <CardTitle>Bearbeiten</CardTitle>
              </CardHeader>
              <CardContent>
                <FundingEdit
                  row={{ ...row, version }}
                  onSaved={update}
                  onReload={reload}
                />
              </CardContent>
            </Card>
          ) : null}
        </div>
        {b ? (
          <Card>
            <CardHeader>
              <CardTitle>Unsere Bewertung</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              <p>
                {b.rang != null ? `Rang ${b.rang} · ` : ""}
                {b.punkte != null
                  ? `${b.punkte} von ${scale?.gesamt ?? ""} Punkten`
                  : "nicht bewertet"}
                {b.spur ? ` · ${b.spur}` : ""}
              </p>
              {b.begruendung ? (
                <p className="text-muted-foreground">{b.begruendung}</p>
              ) : null}
              {b.punkte != null && scale ? (
                <PointsBreakdown scale={scale} rating={b} />
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
};
